// Voice mode, in place of the composer: the orb front and centre (tap it to mute), what it heard
// or what it's doing underneath, and mute / end. The chat above keeps going — what you say and
// what it answers land there as messages too.
import { ChevronDownIcon, MicIcon, XIcon } from "lucide-react";

import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
} from "../ui/menu";
import type { VoiceStatus } from "./useVoiceConversation";
import { VoiceOrb } from "./VoiceOrb";
import { VOICE_OPTIONS, voiceOption } from "./voices";

function statusLine(status: VoiceStatus, name: string, muted: boolean): string {
  if (muted && status === "listening") return "You're muted";
  switch (status) {
    case "connecting":
      return "Connecting…";
    case "listening":
      return "Listening";
    case "thinking":
      return `${name} is thinking…`;
    case "speaking":
      return `${name} is speaking`;
    default:
      return "";
  }
}

export function VoicePanel({
  status,
  caption,
  muted,
  error,
  name,
  color,
  levels,
  voice,
  onVoiceChange,
  onToggleMute,
  onEnd,
  framed = true,
}: {
  status: VoiceStatus;
  caption: string;
  muted: boolean;
  error: string | null;
  /** Who you're talking to ("Morrow", "Atlas"). */
  name: string;
  /** The orb's colour (CSS colour); defaults to the theme's primary. */
  color?: string;
  levels: () => { input: number; output: number };
  /** The voice it speaks with (an Aura-2 speaker id), and picking another. */
  voice: string;
  onVoiceChange: (id: string) => void;
  onToggleMute: () => void;
  onEnd: () => void;
  /** Draw its own card. Off where it sits inside the composer's frame (the main chat). */
  framed?: boolean;
}) {
  const failed = status === "error";
  const heard = caption && (status === "listening" || status === "thinking") ? caption : "";
  const hint =
    status === "thinking" || status === "speaking"
      ? "Talk anytime to interrupt"
      : status === "listening" && !muted
        ? "Go ahead, I'm listening"
        : "";
  return (
    <section
      aria-label={`Voice conversation with ${name}`}
      className={`pointer-events-auto mx-auto flex w-full max-w-[720px] flex-col items-center gap-2.5 px-5 pt-5 pb-4 ${
        framed
          ? "rounded-[28px] border border-border bg-card shadow-[0_2px_14px_rgb(0_0_0/0.06)]"
          : ""
      }`}
    >
      <button
        type="button"
        onClick={onToggleMute}
        disabled={failed || status === "connecting"}
        aria-pressed={muted}
        aria-label={muted ? "Unmute microphone" : "Mute microphone"}
        title={muted ? "Tap to unmute" : "Tap to mute"}
        className="rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-card disabled:cursor-default"
      >
        <VoiceOrb
          size={84}
          status={muted && status === "listening" ? "idle" : status}
          levels={levels}
          {...(color ? { color } : {})}
          className={muted ? "opacity-60" : ""}
        />
      </button>
      <div
        className="flex min-h-11 w-full max-w-[34rem] flex-col items-center text-center"
        aria-live="polite"
      >
        <p
          className={`line-clamp-2 text-[15px] leading-snug text-balance ${
            failed ? "text-destructive" : heard ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          {failed ? (error ?? "Voice stopped") : heard || statusLine(status, name, muted)}
        </p>
        {!failed && hint ? <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      <div className="flex items-center gap-2.5">
        <Menu>
          <MenuTrigger
            render={
              <button
                type="button"
                aria-label={`Voice: ${voiceOption(voice)?.name ?? voice}. Choose another`}
                className="flex h-10 items-center gap-1.5 rounded-full bg-muted px-3.5 text-sm text-foreground transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            }
          >
            <span className="text-muted-foreground">Voice</span>
            <span className="font-medium">{voiceOption(voice)?.name ?? voice}</span>
            <ChevronDownIcon className="size-3.5 opacity-60" />
          </MenuTrigger>
          <MenuPopup side="top" align="center" className="max-h-[22rem] w-72">
            <MenuGroup>
              <MenuGroupLabel>{name}'s voice</MenuGroupLabel>
              <MenuRadioGroup value={voice} onValueChange={(v) => onVoiceChange(String(v))}>
                {VOICE_OPTIONS.map((v) => (
                  <MenuRadioItem key={v.id} value={v.id} className="py-1.5">
                    <span className="flex min-w-0 flex-col">
                      <span className="font-medium">{v.name}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {v.description}
                      </span>
                    </span>
                  </MenuRadioItem>
                ))}
              </MenuRadioGroup>
            </MenuGroup>
          </MenuPopup>
        </Menu>
        <button
          type="button"
          onClick={onToggleMute}
          disabled={failed || status === "connecting"}
          aria-pressed={muted}
          aria-label={muted ? "Unmute microphone" : "Mute microphone"}
          title={muted ? "Unmute" : "Mute"}
          className={`flex size-10 items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 ${
            muted
              ? "bg-destructive/10 text-destructive"
              : "bg-muted text-foreground hover:bg-accent"
          }`}
        >
          <span className="relative flex">
            <MicIcon className="size-[18px]" />
            {muted ? (
              <span
                aria-hidden
                className="absolute top-1/2 left-1/2 h-[2px] w-[22px] -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-current"
              />
            ) : null}
          </span>
        </button>
        <button
          type="button"
          onClick={onEnd}
          className="flex h-10 items-center gap-1.5 rounded-full bg-foreground px-4 text-sm font-medium text-background transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <XIcon className="size-4" />
          End
        </button>
      </div>
    </section>
  );
}
